import { useCallback, useEffect, useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import iconChevronDown from "../../../assets/icon-chevron-down.svg";
import iconFilter from "../../../assets/icon-filter.svg";
import type { AiHubImplementationRecordListFilters } from "../aiHubListFiltersStore";

export type FilterOption = {
  value: string;
  label: string;
};

type DimensionId = "assessmentSource" | "assessmentName" | "impactedObjectType";

type DraftFilters = Pick<
  AiHubImplementationRecordListFilters,
  "assessmentSource" | "assessmentName" | "impactedObjectType"
>;

type ImplementationRecordFilterBarProps = Readonly<{
  keyword: string;
  onKeywordChange: (value: string) => void;
  applied: DraftFilters;
  onApply: (next: DraftFilters) => void;
  sourceOptions: readonly FilterOption[];
  assessmentNameOptions: readonly FilterOption[];
  impactedObjectTypeOptions: readonly FilterOption[];
}>;

const DIMENSIONS: { id: DimensionId; label: string }[] = [
  { id: "assessmentSource", label: "Assessment Source" },
  { id: "assessmentName", label: "Assessment Name" },
  { id: "impactedObjectType", label: "Impacted Item" },
];

export function countImplementationRecordFilters(applied: DraftFilters): number {
  let count = 0;
  if (applied.assessmentSource.length) count += 1;
  if (applied.assessmentName.length) count += 1;
  if (applied.impactedObjectType.length) count += 1;
  return count;
}

const EMPTY_DRAFT: DraftFilters = {
  assessmentSource: [],
  assessmentName: [],
  impactedObjectType: [],
};

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];
}

export default function ImplementationRecordFilterBar({
  keyword,
  onKeywordChange,
  applied,
  onApply,
  sourceOptions,
  assessmentNameOptions,
  impactedObjectTypeOptions,
}: ImplementationRecordFilterBarProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeDimension, setActiveDimension] =
    useState<DimensionId>("assessmentSource");
  const [draft, setDraft] = useState<DraftFilters>(applied);
  const [dimensionQuery, setDimensionQuery] = useState("");

  const open = Boolean(anchorEl);
  const appliedCount = countImplementationRecordFilters(applied);

  useEffect(() => {
    if (open) {
      setDraft(applied);
      setDimensionQuery("");
    }
  }, [applied, open]);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleFilterToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (open) {
        handleClose();
        return;
      }
      handleOpen(event);
    },
    [handleClose, handleOpen, open],
  );

  const optionsForDimension = useMemo(() => {
    switch (activeDimension) {
      case "assessmentSource":
        return sourceOptions;
      case "assessmentName":
        return assessmentNameOptions;
      case "impactedObjectType":
        return impactedObjectTypeOptions;
      default:
        return [];
    }
  }, [
    activeDimension,
    assessmentNameOptions,
    impactedObjectTypeOptions,
    sourceOptions,
  ]);

  const visibleDimensions = useMemo(() => {
    const q = dimensionQuery.trim().toLowerCase();
    if (!q) return DIMENSIONS;
    return DIMENSIONS.filter((dim) => dim.label.toLowerCase().includes(q));
  }, [dimensionQuery]);

  const visibleOptions = useMemo(() => {
    const q = dimensionQuery.trim().toLowerCase();
    if (!q) return optionsForDimension;
    return optionsForDimension.filter(
      (option) =>
        option.label.toLowerCase().includes(q) ||
        option.value.toLowerCase().includes(q),
    );
  }, [dimensionQuery, optionsForDimension]);

  const selectedValues = (dimension: DimensionId): string[] => {
    if (dimension === "assessmentSource") return draft.assessmentSource;
    if (dimension === "assessmentName") return draft.assessmentName;
    return draft.impactedObjectType;
  };

  const setDimensionValue = (dimension: DimensionId, value: string) => {
    setDraft((prev) => {
      if (dimension === "assessmentSource") {
        return {
          ...prev,
          assessmentSource: toggleValue(prev.assessmentSource, value),
        };
      }
      if (dimension === "assessmentName") {
        return {
          ...prev,
          assessmentName: toggleValue(prev.assessmentName, value),
        };
      }
      return {
        ...prev,
        impactedObjectType: toggleValue(prev.impactedObjectType, value),
      };
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap",
        mb: 1.5,
      }}
    >
      <TextField
        value={keyword}
        onChange={(event) => onKeywordChange(event.target.value)}
        placeholder="Input Assmt. ID, Plan ID..."
        size="small"
        sx={{
          flex: 1,
          minWidth: 240,
          "& .MuiOutlinedInput-root": {
            height: 40,
            gap: "12px",
            borderRadius: "4px",
            bgcolor: "#FFFFFF",
            fontSize: 13,
            color: "#101828",
            px: "8px",
            py: "8px",
            "& fieldset": {
              borderWidth: "0.57px",
              borderColor: "rgba(0, 0, 0, 0.1)",
            },
            "&:hover fieldset": {
              borderWidth: "0.57px",
              borderColor: "rgba(0, 0, 0, 0.18)",
            },
            "&.Mui-focused fieldset": {
              borderWidth: "0.57px",
              borderColor: "#295EF1",
            },
            "& .MuiOutlinedInput-input": {
              p: 0,
              height: "auto",
              "&::placeholder": {
                color: "#8C8C8C",
                opacity: 1,
              },
            },
          },
        }}
        slotProps={{
          htmlInput: { "aria-label": "Search implementation records" },
          input: {
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0, ml: 0 }}>
                <SearchIcon sx={{ fontSize: 16, color: "#8C8C8C" }} />
              </InputAdornment>
            ),
            endAdornment: keyword ? (
              <InputAdornment position="end" sx={{ mr: 0 }}>
                <IconButton
                  aria-label="Clear Search"
                  size="small"
                  onClick={() => onKeywordChange("")}
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
      />
      <Button
        variant="text"
        onClick={handleFilterToggle}
        aria-label="Filters"
        aria-expanded={open}
        sx={{
          width: 136,
          minWidth: 136,
          flex: "none",
          height: 40,
          gap: "8px",
          borderRadius: "4px",
          px: "12px",
          textTransform: "none",
          color: "#101828",
          bgcolor: "#FFFFFF",
          fontSize: 13,
          fontWeight: 500,
          justifyContent: "flex-start",
          "&:hover": {
            bgcolor: "#F7F7F7",
          },
        }}
      >
        <Box
          component="img"
          src={iconFilter}
          alt=""
          sx={{ width: 14, height: 14, flex: "none" }}
        />
        <Box
          component="span"
          sx={{
            flex: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          Filters
          {appliedCount > 0 ? (
            <Box
              component="span"
              sx={{
                minWidth: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: "#295EF1",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.5,
              }}
            >
              {appliedCount}
            </Box>
          ) : null}
        </Box>
        <Box
          component="img"
          src={iconChevronDown}
          alt=""
          sx={{
            width: 12,
            height: 12,
            flex: "none",
            transition: "transform 160ms ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 551,
              maxWidth: "calc(100vw - 32px)",
              mt: 1,
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <TextField
            value={dimensionQuery}
            onChange={(event) => setDimensionQuery(event.target.value)}
            placeholder="Input dimension or value..."
            size="small"
            sx={{
              width: 200,
              flex: "none",
              "& .MuiOutlinedInput-root": {
                fontSize: 12,
                letterSpacing: "0px",
              },
              "& .MuiOutlinedInput-input": {
                fontSize: 12,
                letterSpacing: "0px",
                "&::placeholder": {
                  fontSize: 12,
                  letterSpacing: "0px",
                  opacity: 1,
                },
              },
            }}
            slotProps={{
              htmlInput: { "aria-label": "Search filter dimension or value" },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "#8C8C8C" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", minHeight: 220, maxHeight: 360 }}>
          <Box
            sx={{
              width: 200,
              flex: "none",
              borderRight: "1px solid rgba(0,0,0,0.06)",
              overflow: "auto",
            }}
          >
            {visibleDimensions.map((dim) => {
              const active = dim.id === activeDimension;
              const dimCount = selectedValues(dim.id).length;
              return (
                <Box
                  key={dim.id}
                  component="button"
                  type="button"
                  onClick={() => setActiveDimension(dim.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                    border: 0,
                    bgcolor: active ? "rgba(41,94,241,0.04)" : "transparent",
                    textAlign: "left",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    position: "relative",
                    "&::before": active
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 3,
                          bgcolor: "#295EF1",
                        }
                      : undefined,
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "#101828", flex: 1 }}>
                    {dim.label}
                  </Typography>
                  {dimCount > 0 ? (
                    <Box
                      sx={{
                        bgcolor: "rgba(41,94,241,0.06)",
                        color: "#295EF1",
                        borderRadius: "9.5px",
                        minWidth: 23,
                        height: 19,
                        fontSize: 12,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 0.75,
                      }}
                    >
                      {dimCount}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ flex: 1, px: 2.5, py: 1, overflow: "auto" }} role="listbox">
            {visibleOptions.map((option) => {
                const checked = selectedValues(activeDimension).includes(
                  option.value,
                );
                return (
                  <Box
                    key={option.value}
                    role="option"
                    aria-label={option.label}
                    aria-selected={checked}
                    onClick={() => setDimensionValue(activeDimension, option.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      width: "100%",
                      border: 0,
                      bgcolor: "transparent",
                      py: 1.25,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      size="small"
                      sx={{ p: 0 }}
                      onChange={() => undefined}
                    />
                    <Typography sx={{ fontSize: 13, color: "#101828" }}>
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Button
            color="error"
            onClick={() => setDraft(EMPTY_DRAFT)}
            startIcon={<DeleteOutlineIcon />}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Clear All
          </Button>
          <IconButton
            aria-label="Apply Filters"
            onClick={() => {
              onApply(draft);
              handleClose();
            }}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              bgcolor: "#295EF1",
              color: "#fff",
              "&:hover": { bgcolor: "#1B4ED8" },
            }}
          >
            <CheckIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Popover>
    </Box>
  );
}
